import { FunctionComponent } from "react";
import ItemSelectField from "../../../../../ui/new_components/entity_selects/ItemSelectField";
import ProjectSelectField from "../../../../../ui/new_components/entity_selects/ProjectSelectField";
import DcSelectField from "../../../../../ui/new_components/entity_selects/DcSelectField";
import LineItemActions from "./LineItemActions";
import usePOLineItem from "./usePOLineItem";
import { POLineItemCardProps } from "./types";
import { formatAmountWithCommas } from "../../../../../ui/pages/content_panel/utils/helpers";

const POLineItemCardWeb: FunctionComponent<POLineItemCardProps> = (props) => {
  const {
    data,
    editable,
    setEditable,
    readOnly,
    isAddRow,
    isJobWork,
    canAdd,
    defaultProject,
    tax,
    itemSearchDisabled,
    qtyDisabled,
    itemWac,
    showWacWarning,
    onDone,
    cancelEdit,
    remove,
    handleItemSelect,
    handleProjectSelection,
    onQtyChange,
    onPriceChange,
    onLineTypeChange,
    onDcSelect,
  } = usePOLineItem(props);

  const symbol = props.currency?.symbol ?? "";
  const canEditFields = editable && !readOnly;
  const projectName = data?.project?.name ?? defaultProject?.name ?? "--";
  const isService = data.line_type === "service";

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onDone();
      }}
      className={`w-full bg-gray-50 border rounded-xl p-4 space-y-3 font-sans text-xs text-gray-700 ${
        canEditFields ? "border-myPrimaryColor" : "border-gray-200"
      }`}
    >
      {/* Header: item identity + project + amount block (3 columns) */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-start">
        <div className="min-w-0 flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-500">Item Code</label>
            <span className="text-xs text-gray-700">
              {data?.item?.item_code ?? "--"}
            </span>
          </div>

          {canEditFields ? (
            <ItemSelectField
              disabled={itemSearchDisabled}
              value={data.item}
              onSelect={handleItemSelect}
            />
          ) : (
            <div
              className="font-semibold text-xs text-gray-900 break-words"
              title={data?.item?.name ?? ""}
            >
              {data?.item?.name ?? "--"}
            </div>
          )}
          <div className="text-xs text-gray-500 break-words">
            {data?.item?.description ?? "--"}
          </div>
          <div className="text-xs text-gray-500">
            HSN: {data?.item?.tax_detail?.hsn_sac ?? "--"}
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs text-gray-500">Project</label>
          {canEditFields ? (
            <ProjectSelectField
              disabled={readOnly}
              value={data?.project ?? defaultProject ?? null}
              onSelect={handleProjectSelection}
            />
          ) : (
            <div
              className="font-semibold text-xs text-gray-900 truncate"
              title={projectName}
            >
              {projectName}
            </div>
          )}
        </div>

        <div className="text-right">
          <label className="text-xs text-gray-500">Amount</label>
          <div className="font-semibold text-xs text-gray-900">
            {symbol} {formatAmountWithCommas(tax.amount)}
          </div>
        </div>
      </div>

      {/* Input grid: Quantity, Unit Price, Taxes */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="flex flex-col gap-1">
          <label className="text-xs text-gray-500">Quantity</label>
          {canEditFields ? (
            <input
              value={data.unit_no ?? 0}
              disabled={qtyDisabled}
              type="number"
              className="h-9 border rounded-md px-2 text-xs"
              onChange={onQtyChange}
            />
          ) : (
            <div className="h-9 flex items-center border rounded-md px-2 bg-white text-xs">
              {data.unit_no ?? 0}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs text-gray-500">Unit Price</label>
          {canEditFields ? (
            <>
              <input
                value={data.price ?? 0}
                type="number"
                className="h-9 border rounded-md px-2 text-xs"
                onChange={onPriceChange}
              />
              {showWacWarning && (
                <p className="text-xs text-red-600">
                  The usual price for this item is {symbol}{" "}
                  {formatAmountWithCommas(itemWac!)}. Are you sure?
                </p>
              )}
            </>
          ) : (
            <div className="h-9 flex items-center border rounded-md px-2 bg-white text-xs">
              {symbol} {formatAmountWithCommas(data.price ?? 0)}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs text-gray-500">Taxes</label>
          <div className="h-9 flex items-center border rounded-md px-2 bg-white text-xs text-gray-500">
            CGST {tax.cgst} · SGST {tax.sgst} · IGST {tax.igst}
          </div>
        </div>
      </div>

      {/* Job Work fields */}
      {isJobWork && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 border-t pt-3">
          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-500">Type</label>
            <select
              disabled={!editable}
              value={data.line_type ?? ""}
              onChange={(e) => onLineTypeChange(e.target.value)}
              className="h-9 border rounded-md px-2 bg-white w-full text-xs"
            >
              <option value="">Select</option>
              <option value="service">Service Line</option>
              <option value="inventory">Inventory (FG/SFG)</option>
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-500">Flags</label>
            <div className="h-9 flex items-center gap-1">
              {isService ? (
                <>
                  <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                    PAYABLE
                  </span>
                  <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">
                    NO INVENTORY IMPACT
                  </span>
                </>
              ) : (
                <>
                  <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">
                    NON-PAYABLE
                  </span>
                  <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-myPrimaryColor text-white">
                    INV IMPACT
                  </span>
                </>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-500">DC</label>
            {canEditFields ? (
              <DcSelectField
                disabled={readOnly}
                value={data.dc_reference_id ?? ""}
                onSelect={onDcSelect}
              />
            ) : (
              <div className="h-9 flex items-center border rounded-md px-2 bg-white text-xs text-gray-800">
                {data.dc_reference_id ?? "--"}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Footer actions */}
      {!readOnly && (
        <div className="flex flex-wrap gap-2 items-center justify-end border-t pt-3">
          <LineItemActions
            isAddRow={isAddRow}
            editable={editable}
            canAdd={canAdd}
            onEdit={() => setEditable(true)}
            onSave={() => {
              onDone();
              setEditable(false);
            }}
            onCancel={cancelEdit}
            onDelete={remove}
          />
        </div>
      )}
    </form>
  );
};

export default POLineItemCardWeb;
